import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { LuChevronDown } from "react-icons/lu";
import { useAllCategoriesQuery } from "../../Redux/Api/Category";
import { useCiT } from "../../common/ci/i18n";
import { categoryPath } from "../../utils/categoryPath";
import { useGetCompanyProductMutation } from "../../Redux/Api/Admin/Company";
import { useDispatch, useSelector } from "react-redux";
import { clearResults } from "../../Redux/reducer/Filter";
import {
  setAllCategories,
  setAllCompanies,
} from "../../Redux/reducer/toStoreValues";
import { RootState } from "../../Redux/store";
import { CiChipLink } from "../../common/ci/Ui";
import { Category } from "./types";

/**
 * Header ke neeche category chips ki row (design canvas, ticket 2.2).
 *
 * Purana Navbar (Home / Company / Category dropdown) yahi tha; wo categories
 * aur companies ko Redux store me bhi bharta tha — wo kaam yahin rehta hai,
 * kyunki Home aur baaki screens `state.values.allCategories` padhte hain.
 *
 * Chips ka order: pehle jo category log sabse zyada maangte hain (TMT, sheet,
 * pipe, cement…), phir baaki itemCount ke hisaab se. "All N categories" hamesha
 * aakhir me.
 */
const PREFERRED = [
  "TMT BAR",
  "GC SHEET",
  "SQUARE PIPE",
  "ROUND PIPE",
  "FLAT",
  "ANGLE",
  "CEMENT",
  "PPGL",
  "PVC",
  "HARDWARE GATE & DOOR",
  "WELDING ROD",
];

const MAX_CHIPS = 11;

/** "HARDWARE GATE & DOOR" -> "Hardware Gate & Door" */
export const titleCase = (s: string): string =>
  (s || "")
    .toLowerCase()
    .replace(/(^|\s|\(|&\s)([a-z])/g, (m) => m.toUpperCase())
    .replace(/\bTmt\b/g, "TMT")
    .replace(/\bGc\b/g, "GC")
    .replace(/\bGi\b/g, "GI")
    .replace(/\bGp\b/g, "GP")
    .replace(/\bHr\b/g, "HR")
    .replace(/\bCr\b/g, "CR")
    .replace(/\bBp\b/g, "BP")
    .replace(/\bAc\b/g, "AC")
    .replace(/\bPpgl\b/g, "PPGL")
    .replace(/\bPpgi\b/g, "PPGI")
    .replace(/\bPvc\b/g, "PVC")
    .replace(/\bGpsp\b/g, "GPSP")
    .replace(/\bMig\b/g, "MIG")
    .replace(/\bVst\b/g, "VST")
    .replace(/\bSpl\b/g, "SPL");

export const pickChipCategories = (all: Category[] | undefined | null): Category[] => {
  const list = Array.isArray(all) ? all : [];
  const byName = new Map<string, Category>();
  list.forEach((c) => byName.set(String(c?.name || "").trim().toUpperCase(), c));
  const chosen: Category[] = [];
  PREFERRED.forEach((n) => {
    const c = byName.get(n);
    if (c) chosen.push(c);
  });
  if (chosen.length < MAX_CHIPS) {
    [...list]
      .filter((c) => !chosen.includes(c))
      .sort((a, b) => Number(b?.itemCount || 0) - Number(a?.itemCount || 0))
      .forEach((c) => {
        if (chosen.length < MAX_CHIPS) chosen.push(c);
      });
  }
  return chosen;
};

const Navbar = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { data: categoriesData } = useAllCategoriesQuery();
  const [loadCompanies, { data: companyName }] = useGetCompanyProductMutation();
  const allCategories = useSelector(
    (state: RootState) => state.values.allCategories as Category[]
  );

  useEffect(() => {
    loadCompanies({ category: [], search: "" });
  }, [loadCompanies]);

  useEffect(() => {
    if (categoriesData) dispatch(setAllCategories(categoriesData?.data));
  }, [categoriesData, dispatch]);

  useEffect(() => {
    if (companyName) dispatch(setAllCompanies(companyName?.data));
  }, [companyName, dispatch]);

  const chips = useMemo(() => pickChipCategories(allCategories), [allCategories]);
  const t = useCiT();
  const total = Array.isArray(allCategories) ? allCategories.length : 0;

  // /c/:slug ya purana /items/:id/category/:name — abhi kaunsi category khuli hai
  const activeId = (() => {
    const s = location.pathname.match(/^\/c\/([^/]+)/);
    if (s) {
      const hit = chips.find((c) => categoryPath(c) === `/c/${s[1]}`);
      return hit ? hit.id : null;
    }
    const m = location.pathname.match(/^\/items\/([^/]+)\/category\//);
    return m ? decodeURIComponent(m[1]) : null;
  })();

  // Chip ab <Link> hai, navigate() nahi — par purana filter state saaf karna
  // abhi bhi zaroori hai warna nayi category par pichhle filter chipak jaate hain.
  const open = () => dispatch(clearResults());

  if (!chips.length) return null;

  return (
    <nav
      aria-label="Categories"
      className="hidden w-full border-b border-ci-line bg-ci-surface sm:block"
    >
      <div className="mx-auto flex max-w-[1440px] items-center gap-2 overflow-x-auto px-5 py-2.5 no-scrollbar">
        {chips.map((c) => (
          <CiChipLink
            key={c.id}
            to={categoryPath(c)}
            state={{ id: c.id }}
            active={activeId === c.id}
            onClick={open}
          >
            {titleCase(c.name)}
          </CiChipLink>
        ))}
        <CiChipLink dashed to="/categories">
          {t("All categories")}{total ? ` (${total})` : ""} <LuChevronDown size={14} />
        </CiChipLink>
      </div>
    </nav>
  );
};

export default Navbar;
